/**
 * Schedule widgets for the realtor Home dashboard, both fed by the
 * every-open-activity schedule endpoint, so a lead with three bookings shows
 * three rows.
 *
 *   - TodaySchedule: open items due before tomorrow, split into an "Overdue"
 *     group (before local midnight today) and today's items.
 *   - ComingUp: open items from tomorrow until the end of the 7-day window.
 */

import type { UseQueryResult } from "@tanstack/react-query";
import {
	BellRing,
	Home,
	MessageSquare,
	Phone,
	RefreshCw,
	StickyNote,
} from "lucide-react";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type {
	ActivityType,
	ScheduledActivity,
} from "@/features/realtor/services/leads-api.service";
import { itemsFrom, splitDue } from "./dashboard-data";
import { EYEBROW_STYLE, SectionHeading } from "./section-heading";

type ScheduleQuery = UseQueryResult<ScheduledActivity[], Error>;

const ICON_FOR_TYPE: Record<
	ActivityType,
	ComponentType<{ className?: string }>
> = {
	viewing: Home,
	call: Phone,
	reminder: BellRing,
	message: MessageSquare,
	note: StickyNote,
};

const COMING_UP_LIMIT = 4;

export function TodaySchedule({
	query,
	todayStart,
	onOpenLead,
}: {
	query: ScheduleQuery;
	todayStart: Date;
	onOpenLead: (leadId: string) => void;
}) {
	const { t } = useTranslation();
	const { overdue, today } = splitDue(query.data ?? [], todayStart);

	return (
		<Card
			className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]"
			data-testid="today-schedule"
		>
			<CardContent className="p-6 space-y-4">
				<SectionHeading
					eyebrow={t("realtor.dashboard.schedule.eyebrow")}
					title={t("realtor.dashboard.schedule.title")}
					size={24}
				/>
				<ScheduleBody query={query}>
					{overdue.length > 0 && (
						<ScheduleGroup
							heading={t("realtor.dashboard.schedule.overdue")}
							testId="schedule-overdue"
						>
							{overdue.map((item) => (
								<ScheduleRow
									key={item.activity.id}
									item={item}
									withDate
									onClick={() => onOpenLead(item.lead.id)}
								/>
							))}
						</ScheduleGroup>
					)}
					<ScheduleGroup
						heading={
							overdue.length > 0 ? t("realtor.dashboard.schedule.today") : null
						}
						testId="schedule-today"
					>
						{today.length === 0 ? (
							<EmptyNote text={t("realtor.dashboard.schedule.empty")} />
						) : (
							today.map((item) => (
								<ScheduleRow
									key={item.activity.id}
									item={item}
									onClick={() => onOpenLead(item.lead.id)}
								/>
							))
						)}
					</ScheduleGroup>
				</ScheduleBody>
			</CardContent>
		</Card>
	);
}

export function ComingUp({
	query,
	from,
	onOpenLead,
	onViewAll,
}: {
	query: ScheduleQuery;
	/** Start of tomorrow: today's items belong to TodaySchedule. */
	from: Date;
	onOpenLead: (leadId: string) => void;
	onViewAll: () => void;
}) {
	const { t } = useTranslation();
	const items = itemsFrom(query.data ?? [], from, COMING_UP_LIMIT);

	return (
		<Card
			className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]"
			data-testid="coming-up"
		>
			<CardContent className="p-6 space-y-4">
				<header className="flex items-end justify-between">
					<SectionHeading
						eyebrow={t("realtor.dashboard.comingUp.eyebrow")}
						title={t("realtor.dashboard.comingUp.title")}
						size={24}
					/>
					<Button
						variant="ghost"
						size="sm"
						className="h-7 px-3 text-xs text-dashboard-muted hover:text-dashboard-text hover:bg-dashboard-surface"
						onClick={onViewAll}
					>
						{t("realtor.dashboard.viewAll")}
					</Button>
				</header>
				<ScheduleBody query={query}>
					{items.length === 0 ? (
						<EmptyNote text={t("realtor.dashboard.comingUp.empty")} />
					) : (
						<div className="space-y-2">
							{items.map((item) => (
								<ScheduleRow
									key={item.activity.id}
									item={item}
									withDate
									onClick={() => onOpenLead(item.lead.id)}
								/>
							))}
						</div>
					)}
				</ScheduleBody>
			</CardContent>
		</Card>
	);
}

/** Loading and error states shared by both widgets; children once loaded. */
function ScheduleBody({
	query,
	children,
}: {
	query: ScheduleQuery;
	children: React.ReactNode;
}) {
	const { t } = useTranslation();
	if (query.isPending) {
		return <Skeleton className="h-16 w-full rounded-lg bg-dashboard-surface" />;
	}
	if (query.isError) {
		return (
			<div
				role="alert"
				className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-dashboard-border px-3 py-4"
			>
				<p className="text-xs text-dashboard-muted">
					{t("realtor.dashboard.schedule.loadFailed")}
				</p>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="h-7 gap-1.5 px-2 text-xs"
					onClick={() => query.refetch()}
				>
					<RefreshCw className="h-4 w-4" />
					{t("realtor.dashboard.retry")}
				</Button>
			</div>
		);
	}
	return <>{children}</>;
}

function ScheduleGroup({
	heading,
	testId,
	children,
}: {
	heading: string | null;
	testId: string;
	children: React.ReactNode;
}) {
	return (
		<section className="space-y-2" data-testid={testId}>
			{heading && <h3 style={EYEBROW_STYLE}>{heading}</h3>}
			<div className="space-y-2">{children}</div>
		</section>
	);
}

function ScheduleRow({
	item,
	withDate = false,
	onClick,
}: {
	item: ScheduledActivity;
	/** Show the date as well as the time (for items not due today). */
	withDate?: boolean;
	onClick: () => void;
}) {
	const { t, i18n } = useTranslation();
	const { activity, lead } = item;
	const Icon = ICON_FOR_TYPE[activity.type];
	const when = new Date(activity.scheduledAt as string).toLocaleString(
		i18n.language,
		withDate
			? { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }
			: { hour: "numeric", minute: "2-digit" },
	);
	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full flex items-start gap-3 rounded-lg border border-dashboard-border bg-dashboard-paper px-3 py-2.5 hover:bg-dashboard-surface text-left transition-all"
		>
			<span
				role="img"
				aria-label={t(`realtor.leads.activityTypes.${activity.type}`)}
				className="mt-0.5 rounded-full bg-dashboard-pill-awaiting-bg text-dashboard-warning p-1.5 shrink-0"
			>
				<Icon className="h-4 w-4" />
			</span>
			<div className="flex-1 min-w-0">
				<p className="truncate text-sm font-medium text-dashboard-text">
					{activity.title}
				</p>
				<p className="truncate text-xs text-dashboard-muted">{lead.name}</p>
			</div>
			<time
				dateTime={activity.scheduledAt ?? undefined}
				className="shrink-0 text-[11px] text-dashboard-muted"
			>
				{when}
			</time>
		</button>
	);
}

function EmptyNote({ text }: { text: string }) {
	return (
		<div className="rounded-lg border border-dashed border-dashboard-border bg-dashboard-bg/50 px-3 py-6 text-center">
			<p className="text-xs text-dashboard-muted">{text}</p>
		</div>
	);
}
